import { useCallback, useRef } from 'react';
import type { RefObject } from 'react';

import type { CropperCanvas, CropperImage } from 'cropperjs';

import { clampMatrixToCover } from '../coverTransform';

type Props = {
	canvasRef: RefObject<CropperCanvas | null>;
	imageRef: RefObject<CropperImage | null>;
	naturalSizeRef: RefObject<{ width: number; height: number } | undefined>;
};

/**
 * cropperjs v2 doesn't constrain panning the way v1's `viewMode: 3` did, so a drag can leave
 * part of the fixed viewport uncovered (blank/black borders in the exported crop). The returned
 * `transform` listener snaps the image back so it always fully covers the canvas.
 *
 * cropper-image's `$setTransform` emits this (cancelable) event *before* it assigns the new
 * matrix, so `$getTransform()` would still read the previous, in-bounds value here, and any
 * correction made via `$moveTo` would be overwritten as soon as the listener returns. Instead,
 * the proposed matrix is checked, and when it's out of bounds the event is canceled (which makes
 * `$setTransform` bail out) and a clamped version of it is applied in its place.
 */
export function useClampToCover({ canvasRef, imageRef, naturalSizeRef }: Props): (event: Event) => void {
	// The clamp's own `$setTransform()` re-emits `transform` synchronously, which would otherwise
	// re-enter this listener recursively (observed to blow the call stack in production).
	const clampingRef = useRef(false);

	return useCallback(
		(event: Event) => {
			if (clampingRef.current) return;

			const imageElement = imageRef.current;
			const canvas = canvasRef.current;
			const naturalSize = naturalSizeRef.current;
			const { matrix } = (event as CustomEvent<{ matrix?: number[] } | null>).detail ?? {};
			if (!imageElement || !canvas || !naturalSize || !matrix) return;

			const clamped = clampMatrixToCover(matrix, naturalSize, canvas.offsetWidth, canvas.offsetHeight);
			if (!clamped) return;

			event.preventDefault();
			clampingRef.current = true;
			try {
				imageElement.$setTransform(clamped);
			} finally {
				clampingRef.current = false;
			}
		},
		[canvasRef, imageRef, naturalSizeRef],
	);
}
